import { toast } from 'sonner';

import {
  CampaignStatus,
  CampaignTrackingItemType,
  type Campaign,
  type CampaignTrackHandle,
  type CampaignTrackPost,
} from '@/entities/campaign';
import {
  useInvalidateAllCampaigns,
  useRenameCampaignMutation,
  useUpdateCampaignMutation,
} from '@/features/campaign/hooks/mutation';
import { isExactDeepEqual } from '@/shared/utils/is-exact-deep-equal';
import { showDialog } from '@/shared/utils/show-dialog';

import type { CampaignEditorSubmitPayload } from '../../../campaign-editor/campaign-editor-types';
import { buildCampaignEditInitialValues } from '../helpers/build-campaign-edit-initial-valutes';
import { buildCampaignUpdatePayload } from '../helpers/build-campaign-update-payload';

type UseCampaignEditSubmitArgs = {
  campaign: Campaign;
  campaignId: string;
  creators: CampaignTrackHandle[];
  posts: CampaignTrackPost[];
  workspaceTimezone?: string;
  callback: () => void;
};

function buildCreatorTrackKey(creator: { handle: string; platform: string }) {
  return `${creator.platform}:${creator.handle.trim().replace(/^@+/, '').toLowerCase()}`;
}

function hasNewCreatorTrackingItems({
  creators,
  items,
}: {
  creators: CampaignTrackHandle[];
  items: ReturnType<typeof buildCampaignUpdatePayload>['items'];
}) {
  const currentCreatorKeys = new Set(creators.map((creator) => buildCreatorTrackKey(creator)));

  return items.some((item) => {
    if (item.type !== CampaignTrackingItemType.HANDLE) return false;
    return !currentCreatorKeys.has(buildCreatorTrackKey(item));
  });
}

export function useCampaignEditSubmit({
  campaign,
  campaignId,
  creators,
  posts,
  workspaceTimezone,
  callback,
}: UseCampaignEditSubmitArgs) {
  const invalidateAllCampaigns = useInvalidateAllCampaigns();
  const updateCampaignMutation = useUpdateCampaignMutation();
  const renameCampaignMutation = useRenameCampaignMutation();

  const initialValues = buildCampaignEditInitialValues({
    campaign,
    creators,
    posts,
    workspaceTimezone,
  });
  const initialDetailRequest = buildCampaignUpdatePayload({
    campaign,
    creators,
    payload: initialValues,
    posts,
    workspaceTimezone,
  });

  const submitCampaignUpdate = async (payload: CampaignEditorSubmitPayload) => {
    const campaignName = payload.campaignName.trim();
    let detailRequest;

    try {
      detailRequest = buildCampaignUpdatePayload({
        campaign,
        creators,
        payload,
        posts,
        workspaceTimezone,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to prepare campaign update.');
      return;
    }

    const shouldRenameCampaign = campaignName !== campaign.name.trim();
    const shouldUpdateCampaignDetail = !isExactDeepEqual(detailRequest, initialDetailRequest);
    const shouldShowActiveCreatorTrackingNotice =
      campaign.status === CampaignStatus.ACTIVE &&
      hasNewCreatorTrackingItems({ creators, items: detailRequest.items });

    if (!shouldRenameCampaign && !shouldUpdateCampaignDetail) {
      callback();
      return;
    }

    const operations: Array<Promise<unknown>> = [];

    if (shouldUpdateCampaignDetail) {
      operations.push(updateCampaignMutation.mutateAsync({ campaignId, request: detailRequest }));
    }

    if (shouldRenameCampaign) {
      operations.push(renameCampaignMutation.mutateAsync({ campaignId, campaignName }));
    }

    const results = await Promise.allSettled(operations);
    const hasFailedOperation = results.some((result) => result.status === 'rejected');

    if (hasFailedOperation) {
      const hasSucceededOperation = results.some((result) => result.status === 'fulfilled');
      if (hasSucceededOperation) {
        await invalidateAllCampaigns({ campaignId, includeDetail: true });
        toast.error(
          'Some campaign changes were saved. Please review the updated values and try again.',
        );
        return;
      }
      return;
    }

    callback();
    await invalidateAllCampaigns({ campaignId, includeDetail: true });
    toast.success(shouldRenameCampaign ? 'Campaign updated.' : 'Campaign details updated.');

    if (shouldShowActiveCreatorTrackingNotice) {
      void showDialog({
        title: 'Tracking for new creators starts tomorrow',
        message:
          'Newly added creators will start appearing from tomorrow. Posts published today will still be collected, so you won’t miss today’s content.',
        confirmLabel: 'Confirm',
        hideCancel: true,
      });
    }
  };

  return {
    isSubmitting: updateCampaignMutation.isPending || renameCampaignMutation.isPending,
    submitCampaignUpdate,
  };
}
